import React, { useMemo, useState } from "react";
import { Building2, Check, PackageSearch, Plus, SlidersHorizontal, Tag } from "lucide-react";
import VirtualTable from "../../../components/custom/VirtualTable";
import { AttributeDefinition, Department, InventoryItem, InventoryRow, ItemType } from "./types";
import { EmptyState } from "./shared";
import {
    Card,
    Chip,
    ChipRow,
    ClearButton,
    CountSpan,
    DeptGroupNav,
    DeptHeaderNav,
    FilterGroupLabel,
    FilterGroups,
    FiltersHeader,
    FiltersTitle,
    HelpText,
    Input,
    MonoStrong,
    Muted,
    NavButton,
    NavList,
    PrimaryButton,
    ResultCount,
    ResultsCol,
    ResultsHeader,
    Sidebar,
    Split,
} from "./styles";
import ActionIcon from "../../../components/custom/ActionIcon";
import apiClient from "../../../apiClient";
import useUnit from "../../../customHooks/useUnit";
import useExportData from "./useExportData";
import { useSerialsModalDataStore } from "./SerialsModal";

const valueToString = (def: AttributeDefinition | undefined, raw: unknown): string => {
    if (raw === undefined || raw === null || raw === "") return "—";
    if (def && def.dataType === "bool") return raw ? "Yes" : "No";
    return String(raw);
};

const filterOptionsFor = (def: AttributeDefinition): string[] => {
    if (def.dataType === "list") return def.options;
    if (def.dataType === "bool") return ["Yes", "No"];
    return [];
};

type TypeFilter = number | "all";

interface InventoryViewProps {
    items: InventoryItem[];
    departments: Department[];
    itemTypes: ItemType[];
    defsForType: (typeId: number) => AttributeDefinition[];
    typeById: (id: number) => ItemType | undefined;
    deptForType: (typeId: number) => Department | undefined;
    onRowClick: (row: InventoryRow) => void;
    onAddItem: () => void;
    handleAddQty: (id: string) => void;
}

export default function InventoryView({ items, departments, itemTypes, defsForType, typeById, deptForType, onRowClick, onAddItem, handleAddQty }: InventoryViewProps) {
    const [selectedTypeId, setSelectedTypeId] = useState<TypeFilter>("all");
    const [typeQuery, setTypeQuery] = useState("");
    const [filters, setFilters] = useState<Record<string, string[]>>({});
    const [sortKey, setSortKey] = useState<"name" | "quantity">("quantity");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

    const activeDefs = selectedTypeId === "all" ? [] : defsForType(selectedTypeId).filter((d) => d.isFilterable);

    const selectType = (id: TypeFilter) => {
        setSelectedTypeId(id);
        setFilters({});
    };

    const toggleFilter = (key: string, value: string) => {
        setFilters((prev) => {
            const current = prev[key] || [];
            const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
            const copy = { ...prev };
            if (next.length === 0) delete copy[key];
            else copy[key] = next;
            return copy;
        });
    };

    const filtered = useMemo(() => {
        return items.filter((item) => {
            if (selectedTypeId !== "all" && item.itemTypeId !== selectedTypeId) return false;
            return Object.entries(filters).every(([key, values]) => {
                if (values.length === 0) return true;
                const def = defsForType(item.itemTypeId).find((d) => d.attributeKey === key);
                return values.includes(valueToString(def, item.attributes[key]));
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, selectedTypeId, filters]);

    const sorted = useMemo(() => {
        const copy = [...filtered];
        copy.sort((a, b) => {
            const cmp = sortKey === "name" ? a.name.localeCompare(b.name) : a.availableQuantity - b.availableQuantity;
            return sortDir === "asc" ? cmp : -cmp;
        });
        return copy;
    }, [filtered, sortKey, sortDir]);

    const activeFilterCount = Object.values(filters).reduce((s, v) => s + v.length, 0);

    const viewSerials = useSerialsModalDataStore(s => s.viewSerials);
    // Build the VirtualTable `mapping` dynamically. Attribute columns come from
    // the selected type's definitions; in the "all" view (attributes differ per
    // type) they collapse into a single summary column.
    const columns = useMemo(() => {
        const cols: Array<{ name: string; value: keyof InventoryRow; number?: boolean; icon?: string; function?: (item: any) => void; }> = [{ name: "Name", value: "name" }];
        if (selectedTypeId === "all") {
            cols.push({ name: "Type", value: "typeName" });
            cols.push({ name: "Department", value: "deptName" });
            cols.push({ name: "Qty", value: "quantity", number: true });
            cols.push({ name: "Attributes", value: "attributeSummary" });
        } else {
            cols.push({ name: "Qty", value: "quantity", number: true });
            defsForType(selectedTypeId).forEach((d) => {
                cols.push({ name: d.label, value: d.attributeKey });
            });
        }
        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTypeId, itemTypes]);

    // VirtualTable reads each column's `value` as a flat property, so hoist the
    // nested attribute values onto the row keyed by attributeKey.
    const rows: InventoryRow[] = sorted.map((item) => {
        const itemDefs = defsForType(item.itemTypeId);
        const row: InventoryRow = {
            id: item.id,
            name: item.name,
            quantity: item.availableQuantity,
            typeName: typeById(item.itemTypeId)?.name ?? "",
            deptName: deptForType(item.itemTypeId)?.name ?? "",
            attributeSummary: "",
        };
        itemDefs.forEach((d) => {
            row[d.attributeKey] = valueToString(d, item.attributes[d.attributeKey]);
        });
        row.attributeSummary = itemDefs
            .map((d) => {
                const v = valueToString(d, item.attributes[d.attributeKey]);
                return v === "—" ? null : `${d.label}: ${v}`;
            })
            .filter(Boolean)
            .join(" · ");
        return row;
    });

    const { exportData } = useExportData();

    return (
        <Split>
            <Sidebar>
                <Card>
                    <Input value={typeQuery} onChange={(e) => setTypeQuery(e.target.value)} placeholder="Search items…" />
                    <NavList style={{ marginTop: 12 }}>
                        <NavButton active={selectedTypeId === "all"} onClick={() => selectType("all")}>
                            <PackageSearch size={16} />
                            <span>All items</span>
                            <CountSpan>{items.length}</CountSpan>
                        </NavButton>

                        {departments.map((dept) => {
                            const query = typeQuery.trim().toLowerCase();
                            const types = itemTypes.filter(
                                (t) => t.departmentId === dept.id && (!query || t.name.toLowerCase().includes(query))
                            );
                            if (types.length === 0) return null;
                            return (
                                <DeptGroupNav key={dept.id}>
                                    <DeptHeaderNav>
                                        <Building2 size={12} />
                                        {dept.name}
                                    </DeptHeaderNav>
                                    {types.map((t) => {
                                        const count = items.filter((i) => i.itemTypeId === t.id).length;
                                        return (
                                            <NavButton key={t.id} active={selectedTypeId === t.id} onClick={() => selectType(t.id)}>
                                                <Tag size={16} />
                                                <span>{t.name}</span>
                                                <CountSpan>{count}</CountSpan>
                                            </NavButton>
                                        );
                                    })}
                                </DeptGroupNav>
                            );
                        })}
                    </NavList>
                </Card>

                <Card style={{ marginTop: 16 }}>
                    <FiltersHeader>
                        <FiltersTitle>
                            <SlidersHorizontal size={14} />
                            Filters
                        </FiltersTitle>
                        {activeFilterCount > 0 && <ClearButton onClick={() => setFilters({})}>Clear ({activeFilterCount})</ClearButton>}
                    </FiltersHeader>

                    {selectedTypeId === "all" ? (
                        <HelpText>Filter by attributes.</HelpText>
                    ) : activeDefs.length === 0 ? (
                        <HelpText>No filterable attributes.</HelpText>
                    ) : (
                        <FilterGroups>
                            {activeDefs.map((def) => (
                                <div key={def.id}>
                                    <FilterGroupLabel>{def.label}</FilterGroupLabel>
                                    <ChipRow>
                                        {filterOptionsFor(def).map((opt) => {
                                            const on = (filters[def.attributeKey] || []).includes(opt);
                                            return (
                                                <Chip key={opt} on={on} onClick={() => toggleFilter(def.attributeKey, opt)}>
                                                    {on && <Check size={12} />}
                                                    {opt}
                                                </Chip>
                                            );
                                        })}
                                    </ChipRow>
                                </div>
                            ))}
                        </FilterGroups>
                    )}
                </Card>
            </Sidebar>

            <ResultsCol>
                <ResultsHeader>
                    <ResultCount>
                        <MonoStrong>{sorted.length}</MonoStrong> {sorted.length === 1 ? "result" : "results"}
                        {selectedTypeId !== "all" && <Muted> · {typeById(selectedTypeId)?.name}</Muted>}
                    </ResultCount>
                    <PrimaryButton sm onClick={onAddItem} style={{ marginLeft: "auto" }}>
                        <Plus size={16} />
                        Add item
                    </PrimaryButton>
                    <ActionIcon
                        type={'csv'}
                        onClick={() => exportData()}
                    />
                </ResultsHeader>

                {sorted.length === 0 ? (
                    <EmptyState title="Nothing matches these filters" body="Loosen a filter, or clear them to see the full list for this type." />
                ) : (
                    <VirtualTable<InventoryRow> 
                        data={rows} 
                        // @ts-ignore 
                        mapping={columns} 
                        handleClick={item => viewSerials(item.id, `for ${item.typeName} | ${item.name}`, 'units') } 
                    />
                )}
            </ResultsCol>
        </Split>
    );
}
